import { Injectable } from '@angular/core';
import { Plugins } from '@capacitor/core';
import { QueryRef } from 'apollo-angular';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AddMessageGQL,
  IncidentSetStatusWithMessageGQL,
  CreateIncidentGQL,
  CreateIncidentResponse,
  CreateAnonymousIncidentGQL,
  GetIncidentByCaseIdGQL,
  GetMessagesByIncidentIdGQL,
  Incident,
  ListIncidentsGQL,
  Message,
  Tag,
  User,
  AddAnonymousMessageGQL,
  UpdateAssigneesGQL,
  Status,
  Attachment,
  GetAttachmentsByIncidentIdGQL,
  GetOneTimeCodeForIncidentByPhoneGQL,
  GetOneTimeCodeForIncidentByPasswordGQL,
  GetIncidentByOneTimeCodeAndCaseIdGQL,
  GetMessagesByOneTimeCodeAndCaseIdGQL,
  OneTimeCode,
  TranslateMessageGQL,
  TranslateMessageAnonymousGQL,
  ListCategoriesGQL,
  ListCategoriesAnonymousGQL,
  ListIncidentsQuery,
  Submission,
  NestedAttachment,
  GetReporterSettingsAnonymousGQL,
  ReporterType,
  ReporterSettings,
  ReporterLoginType,
  ListActiveCategoriesGQL,
  ListActiveCategoriesAnonymousGQL,
  Tenant,
  GetTenantGQL
} from 'src/generated/graphql';
import { CurrentFilters } from '../admin/admin-inbox-filters/admin-inbox-filters.component';
import { Sender } from './sender.enum';
import { Router } from '@angular/router';

const { Storage } = Plugins;

@Injectable({
  providedIn: 'root',
})
export class IncidentService {
  private incident: Incident;
  public activeAdminIncidentKey = 'activeAdminIncident';
  private activeIncidentChangedSource = new Subject<Incident>();
  public activeIncidentChanged =
    this.activeIncidentChangedSource.asObservable();

  reporterType = ReporterType.Form;
  reporterLoginType = ReporterLoginType.Password;

  constructor(
    private readonly getTenantGQL: GetTenantGQL,
    private readonly createIncidentGQL: CreateIncidentGQL,
    private readonly createAnonymousIncidentGQL: CreateAnonymousIncidentGQL,
    private readonly addMessageGQL: AddMessageGQL,
    private readonly incidentSetStatusWithMessageGQL: IncidentSetStatusWithMessageGQL,
    private readonly addAnonymousMessageGQL: AddAnonymousMessageGQL,
    private readonly getIncidentByCaseIdGQL: GetIncidentByCaseIdGQL,
    private readonly getMessagesByIncidentIdGQL: GetMessagesByIncidentIdGQL,
    private readonly getAttachmentsByIncidentIdGQL: GetAttachmentsByIncidentIdGQL,
    private readonly listIncidentsGql: ListIncidentsGQL,
    private readonly updateAssignesGql: UpdateAssigneesGQL,
    private readonly getOneTimeCodeForIncidentByPhoneGql: GetOneTimeCodeForIncidentByPhoneGQL,
    private readonly getOneTimeCodeForIncidentByPasswordGql: GetOneTimeCodeForIncidentByPasswordGQL,
    private readonly getIncidentByOneTimeCodeAndCaseIdGql: GetIncidentByOneTimeCodeAndCaseIdGQL,
    private readonly getMessagesByOneTimeCodeAndCaseIdGQL: GetMessagesByOneTimeCodeAndCaseIdGQL,
    private readonly translateMessageAnonymousGQL: TranslateMessageAnonymousGQL,
    private readonly translateMessageGQL: TranslateMessageGQL,
    public readonly listCategoriesGQL: ListCategoriesGQL,
    private readonly listCategoriesAnonymousGQL: ListCategoriesAnonymousGQL,
    public readonly listActiveCategoriesGQL: ListActiveCategoriesGQL,
    private readonly listActiveCategoriesAnonymousGQL: ListActiveCategoriesAnonymousGQL,
    public readonly getReporterSettingsAnonymousGQL: GetReporterSettingsAnonymousGQL,
    private readonly router: Router,
  ) {}

  async getOneTimeCodeForIncidentByPhone(
    caseId: string,
    reporterPhone: string,
  ): Promise<boolean> {
    const response = await this.getOneTimeCodeForIncidentByPhoneGql
      .fetch(
        {
          caseId: caseId,
          reporterPhone: reporterPhone,
        },
        { fetchPolicy: 'network-only' },
      )
      .toPromise();

    return response.data.getOneTimeCodeForIncidentByPhone;
  }

  async getOneTimeCodeForIncidentByPassword(
    caseId: string,
    password: string,
  ): Promise<OneTimeCode> {
    const response = await this.getOneTimeCodeForIncidentByPasswordGql
      .fetch(
        {
          caseId: caseId,
          password: password,
        },
        { fetchPolicy: 'network-only' },
      )
      .toPromise();

    return response.data.getOneTimeCodeForIncidentByPassword;
  }

  // Used by reporter flow
  async newIncident(tag: Tag): Promise<void> {
    const incident: Incident = this.createEmptyIncident();
    incident.tags = [tag];
    this.incident = incident;
    await this.saveIncident('incident', this.incident);
  }

  async updateAssignees(
    incident: Incident,
    assignees: User[],
  ): Promise<Incident> {
    //Additional variable that graphql complains about

    const tempAssignees = [];
    for (const assignee of assignees) {
      const temp = { ...assignee };
      delete temp['__typename'];
      tempAssignees.push(temp);
    }

    const response = await this.updateAssignesGql
      .mutate({
        incidentId: incident.id,
        assignees: tempAssignees,
      })
      .toPromise();

    const updatedIncident = {
      ...this.createEmptyIncident(),
      ...response.data.updateAssignees,
    };
    this.saveIncident(this.activeAdminIncidentKey, updatedIncident);
    return updatedIncident;
  }

  /**
   * Entrypoint for creating and persisting a case to the API.
   * TODO This is really something the API should be doing but for now this logic was placed in the UI, also once solved how we can use translateService in this service without circular depencecy errors we can skip noPhoneNumberMessage parameter
   * @param incident
   * @param reporterPhone
   * @param anonymous
   * @param firstReporterMessage
   * @returns
   */
  async saveIncidentToBackend(
    incident: Incident,
    reporterPhone: string,
    anonymous: boolean,
    firstReporterMessage: string,
  ): Promise<CreateIncidentResponse> {
    // Persist the incident in API which returns a real _id and caseId.
    const response = await this._persistIncident(
      incident,
      reporterPhone,
      anonymous,
      firstReporterMessage,
    );

    this.incident = { ...this.incident, ...response.incident };
    await this.saveIncident('incident', this.incident);
    return { incident: this.incident, viewPassword: response.viewPassword };
  }

  /**
   * Creates a new incident (Case) in the Api returning a real _id and caseId
   * Uses two different mutations if the user is anonymous or not
   * @param incident
   * @param reporterPhone
   * @param anonymous
   * @param messages
   * @returns Incident
   */
  private async _persistIncident(
    incident: Incident,
    reporterPhone: string,
    anonymous: boolean,
    caseMessage: string,
  ): Promise<CreateIncidentResponse> {
    incident.tags = incident.tags
      .map((t: Tag) => JSON.parse(JSON.stringify(t)))
      .map((t: Tag) => {
        delete t['__typename'];
        t.conversationalFlow.map((entry) => delete entry['__typename']);
        return t;
      });

    if (anonymous) {
      const response = await this.createAnonymousIncidentGQL
        .mutate({
          restrictedUsers: incident.restrictedUsers
            ? incident.restrictedUsers.map((restrictedUser) => {
                delete restrictedUser['__typename'];
                return restrictedUser;
              })
            : [],
          tags: incident.tags,
          submissions: incident.submissions,
          firstMessage: caseMessage,
          reporterPhone: reporterPhone,
        })
        .toPromise();
      return response.data.createAnonymousIncident;
    } else {
      const response = await this.createIncidentGQL
        .mutate({
          tags: incident.tags,
          submissions: incident.submissions,
          firstMessage: caseMessage,
        })
        .toPromise();
      return response.data.createIncident;
    }
  }

  /**
   * Creates a new message in the API
   * TODO merge with saveMessageToBackend()
   * @param message
   * @param incidentId
   */
  private async _persistMessage(
    message: Message,
    incidentId: string,
    anonymous: boolean,
  ): Promise<void> {
    const input = {
      message: message.text,
      incidentId,
      sender: message.sender,
      fileUploads: message.attachments.map((a) => {
        return { contentType: a.contentType, key: a.key, name: a.name };
      }), // Array is of type Attachment but should be AddAttachmentInput
    };

    if (anonymous) {
      await this.addAnonymousMessageGQL.mutate(input).toPromise();
    } else {
      await this.addMessageGQL.mutate(input).toPromise();
    }
  }

  async saveMessageToBackend(
    msg: Message,
    incident: Incident,
    anonymous = false,
  ): Promise<Message> {
    if (anonymous) {
      const response = await this.addAnonymousMessageGQL
        .mutate({
          message: msg.text,
          incidentId: incident.id,
          sender: msg.sender,
          fileUploads: msg.attachments.map((a) => {
            return { contentType: a.contentType, key: a.key, name: a.name };
          }), // Array is of type Attachment but should be AddAttachmentInput
        })
        .toPromise();

      //TODO make sure that this message is synced, Updates to Graphql has made this unnessesary
      return {
        ...this.createEmptyMessage(),
        ...response.data.addAnonymousMessage,
      };
    } else {
      const response = await this.addMessageGQL
        .mutate({
          message: msg.text,
          incidentId: incident.id,
          sender: msg.sender,
          fileUploads: msg.attachments.map((a) => {
            return { contentType: a.contentType, key: a.key, name: a.name };
          }), // Array is of type Attachment but should be AddAttachmentInput
        })
        .toPromise();

      //TODO make sure that this message is synced, Updates to Graphql has made this unnessesary
      return { ...this.createEmptyMessage(), ...response.data.addMessage };
    }
  }

  async incidentSetStatusWithMessage(
    status: Status,
    msg: Message,
    incident: Incident,
  ): Promise<Message> {
    const response = await this.incidentSetStatusWithMessageGQL
      .mutate({
        message: msg.text,
        incidentId: incident.id,
        sender: msg.sender,
        status: status,
      })
      .toPromise();

    await this.saveIncident(
      this.activeAdminIncidentKey,
      response.data.incidentSetStatusWithMessage.incident,
    );

    //TODO make sure that this message is synced, Updates to Graphql has made this unnessesary
    return {
      ...this.createEmptyMessage(),
      ...response.data.incidentSetStatusWithMessage.message,
    };
  }

  // These are just used by reporter incident main ATM, Clean this
  async getIncident(name: string): Promise<Incident> {
    try {
      const data = await Storage.get({ key: name });
      this.incident = JSON.parse(data.value) as Incident;
      return this.incident;
    } catch (e) {
      console.error(e);
      this.incident = this.createEmptyIncident();
      return this.incident;
    }
  }

  async deleteIncident(name: string): Promise<void> {
    try {
      await Storage.remove({ key: name });
    } catch (e) {
      console.error(e);
    } finally {
      this.incident = this.createEmptyIncident();
    }
  }

  // These are just used by reporter incident main ATM, Clean this
  async saveIncident(name: string, incident: Incident): Promise<Incident> {
    await Storage.set({
      key: name,
      value: JSON.stringify(incident),
    });
    this.incident = incident;
    this.activeIncidentChangedSource.next(this.incident);
    return this.incident;
  }

  async saveTenantID(tenantId: string): Promise<void> {
    if (!!tenantId && tenantId?.length > 10) {
      await Storage.set({
        key: 'tenant-id',
        value: JSON.stringify(tenantId),
      });
    } else {
      alert("Couldn't identify your workspace, please try the link again");
      throw new Error('Invalid tenant id');
    }
  }

  async getTenantID(): Promise<string> {
    try {
      const data = await Storage.get({ key: 'tenant-id' });
      return JSON.parse(data.value) as string;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getReporterSettings(
    tenantId: string,
  ): Promise<ReporterSettings> {
    if (!!tenantId && tenantId?.length > 10) {
      const response = await this.getReporterSettingsAnonymousGQL
        .fetch({ tenantId })
        .toPromise();

      this.reporterType = response.data.getReporterSettingsAnonymous.type;
      this.reporterLoginType =
        response.data.getReporterSettingsAnonymous.loginType;

      return response.data.getReporterSettingsAnonymous;
    } else {
      alert("Couldn't identify your workspace, please try the link again");
      throw new Error('Invalid tenant id');
    }
  }

  async reporterTypeRedirect(
    tenantId: string,
    reporterType: string,
  ): Promise<void> {
    await this.getReporterSettings(tenantId);
    if (reporterType !== this.reporterType) {
      switch (this.reporterType) {
        case ReporterType.Form:
          await this.router.navigate(['incident-report'], {
            queryParamsHandling: 'merge',
          });
          break;
        default:
          await this.router.navigate(['incident-main'], {
            queryParamsHandling: 'merge',
          });
      }
    }
  }

  async getIncidentByCaseId(caseId: string): Promise<Incident> {
    const response = await this.getIncidentByCaseIdGQL
      .fetch({
        caseId: caseId,
      }, {fetchPolicy: 'network-only'})
      .toPromise();

    return response.data.getIncidentByCaseId;
  }

  private oneTimeCodeAndCaseId: {
    oneTimeCode: string;
    caseId: string;
  };

  setOneTimeCodeAndCaseId(oneTimeCode: string, caseId: string): void {
    if (!!oneTimeCode && !!caseId) {
      this.oneTimeCodeAndCaseId = {
        oneTimeCode,
        caseId,
      };
    } else {
      this.oneTimeCodeAndCaseId = undefined;
    }
  }

  async unauthorizeReporter(): Promise<void> {
    await this.deleteIncident('incident');
    this.oneTimeCodeAndCaseId = undefined;
  }

  getOneTimeCodeAndCaseId(): {
    oneTimeCode: string;
    caseId: string;
  } {
    return this.oneTimeCodeAndCaseId;
  }

  isOneTimeCodeAndCaseIdSet(): boolean {
    return !!this.oneTimeCodeAndCaseId;
  }

  async getIncidentByOneTimeCodeAndCaseId(
    oneTimeCode: string,
    caseId: string,
  ): Promise<Incident> {
    const response = await this.getIncidentByOneTimeCodeAndCaseIdGql
      .fetch({
        oneTimeCode: oneTimeCode,
        caseId: caseId,
      })
      .toPromise();

    return response.data.getIncidentByOneTimeCodeAndCaseId;
  }

  getMessagesByIncidentIdObservableWithPolling(
    incidentId: string,
  ): Observable<Message[]> {
    return this.getMessagesByIncidentIdGQL
      .watch({ incidentId }, { pollInterval: 10000 })
      .valueChanges.pipe(
        map((results) => results.data.getMessagesByIncidentId),
      );
  }

  getMessagesByOneTimeCodeAndCaseIdObservableWithPolling(
    oneTimeCode: string,
    caseId: string,
  ): Observable<Message[]> {
    return this.getMessagesByOneTimeCodeAndCaseIdGQL
      .watch(
        {
          oneTimeCode,
          caseId,
        },
        { pollInterval: 10000, fetchPolicy: 'network-only' },
      )
      .valueChanges.pipe(
        map((results) => results.data.getMessagesByOneTimeCodeAndCaseId),
      );
  }

  async translateText(text: string, anonymous: boolean): Promise<string> {
    if (anonymous) {
      const query = await this.translateMessageAnonymousGQL
        .fetch({ message: text })
        .toPromise();
      return query.data.translateMessageAnonymous;
    } else {
      const query = await this.translateMessageGQL
        .fetch({ message: text })
        .toPromise();
      return query.data.translateMessage;
    }
  }

  async getAttachmentsByIncidentId(id: string): Promise<Attachment[]> {
    const response = await this.getAttachmentsByIncidentIdGQL
      .fetch(
        {
          incidentId: id,
        },
        { fetchPolicy: 'network-only' },
      )
      .toPromise();

    return response.data.getAttachmentsByIncidentId;
  }

  getIncidentsObservableWithPolling(currentFilter: Partial<CurrentFilters>): {
    queryRef: QueryRef<
      ListIncidentsQuery,
      {
        sort?: string;
        assigneeIds?: string[];
        categoryIds?: string[];
      }
    >;
    observable: Observable<Incident[]>;
  } {
    const queryRef = this.listIncidentsGql.watch(currentFilter, {
      pollInterval: 30000,
    });
    return {
      queryRef,
      observable: queryRef.valueChanges.pipe(
        map((results) => results.data.listIncidents),
      ),
    };
  }

  getTenant(): Observable<Tenant> {
    return this.getTenantGQL.watch({}, { fetchPolicy: 'network-only' })
    .valueChanges.pipe(map((result) => {
      return result.data.getTenant
    }));
  }

  listCategories(anonymous: boolean): Observable<Tag[]> {
    if (anonymous) {
      return this.listCategoriesAnonymousGQL
        .fetch({}, { fetchPolicy: 'network-only' })
        .pipe(map((result) => result.data.listCategoriesAnonymous));
    } else {
      return this.listCategoriesGQL
        .watch({}, { fetchPolicy: 'network-only' })
        .valueChanges.pipe(map((result) => {
          return result.data.listCategories
        }));
    }
  }

  listActiveCategories(anonymous: boolean): Observable<Tag[]> {
    if (anonymous) {
      return this.listActiveCategoriesAnonymousGQL
        .fetch({}, { fetchPolicy: 'network-only' })
        .pipe(map((result) => result.data.listActiveCategoriesAnonymous));
    } else {
      return this.listActiveCategoriesGQL
        .watch({}, { fetchPolicy: 'network-only' })
        .valueChanges.pipe(map((result) => result.data.listActiveCategories));
    }
  }

  async getIncidents(): Promise<Incident[]> {
    const response = await this.listIncidentsGql
      .fetch({}, { fetchPolicy: 'network-only' })
      .toPromise();

    return response.data.listIncidents;
  }

  generateCaseFirstMessage(message: string): string {
    const maxCharacterCount = 160;
    let caseMessage = message.trim();
    if (caseMessage.length > maxCharacterCount) {
      caseMessage = caseMessage.substring(0, maxCharacterCount - 3) + '...';
    }

    // GraphQL will deny an empty string
    if (caseMessage.length === 0) {
      caseMessage = ' ';
    }

    return caseMessage;
  }

  createSubmissionFromReporter(
    messageString: string,
    qustion: string,
    fileUploads: NestedAttachment[],
  ): Submission {
    const submission: Submission = {
      answer: messageString,
      question: qustion,
      attachments: fileUploads,
    };
    return submission;
  }

  createMessageFromReporter(
    messageString: string,
    fileUploads: Attachment[],
  ): Message {
    const message: Message = this.createEmptyMessage();
    message.text = messageString;
    message.sender = Sender.REPORTER;
    message.attachments = fileUploads;
    return message;
  }

  createMessageFromAdmin(
    messageString: string,
    fileUploads: Attachment[],
  ): Message {
    const message: Message = this.createEmptyMessage();
    message.text = messageString;
    message.sender = Sender.ADMIN;
    message.attachments = fileUploads;
    return message;
  }

  createMessageFromBot(messageString: string): Message {
    const message: Message = this.createEmptyMessage();
    message.text = messageString;
    message.sender = Sender.BOT;
    return message;
  }

  createEmptyIncident(): Incident {
    const incident: Incident = {
      id: undefined,
      tags: [],
      submissions: [],
      caseId: '',
      firstMessage: '',
      status: Status.New,
      updatedAt: undefined,
      createdAt: undefined,
      assignees: [],
    };

    return incident;
  }

  createEmptyMessage(): Message {
    const message: Message = {
      sender: undefined,
      attachments: [],
      id: undefined,
      incident: undefined,
      text: undefined,
      type: undefined,
      updatedAt: undefined,
      createdAt: undefined,
      parentId: undefined,
    };

    return message;
  }

  getIncidentStatusClass(incident: Incident): string {
    return `status-${incident ? incident.status : Status.New}`;
  }
}
